'use client';

import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCardSkeleton } from './blog-cart-skeleton';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useState } from 'react';

const ALL_CATEGORIES_TAB = 'all';

export function BlogsList({ locale, categories }: { locale: string; categories: BlogCategory[] }) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 9,
          order: 'desc',
          orderBy: 'created_at',
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery?.isLoading;

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      className={cn('custom-section', 'gap-12 pb-12 pr-0 pt-12 md:pr-4')}
    >
      <TabsList
        className={cn(
          'custom-container',
          'flex h-fit w-full flex-nowrap items-start justify-start gap-2 rounded-none bg-transparent p-0 md:gap-3',
        )}
      >
        <TabsTrigger value={ALL_CATEGORIES_TAB} className={cn('ea-blogs-list__tabs-trigger')}>
          {t('text2000')}
        </TabsTrigger>
        {categories.map(category => (
          <TabsTrigger
            key={category.id}
            value={String(category.id)}
            className={cn('ea-blogs-list__tabs-trigger')}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {[ALL_CATEGORIES_TAB, ...categories.map(category => String(category.id))]
        .filter(value => value !== activeTab)
        .map(value => (
          <TabsContent key={value} value={value} forceMount hidden />
        ))}
      <TabsContent value={activeTab} className={cn('custom-container', 'pr-4 md:pr-0')}>
        <div
          className={cn(
            'custom-container',
            'grid grid-cols-1 gap-x-3 gap-y-6 sm:grid-cols-2 md:gap-y-8 lg:grid-cols-3 lg:gap-y-12',
          )}
        >
          {blogsQuery.error && (
            <Paragraph
              className="col-span-full w-full pt-8 text-center"
              size="base"
              color="neutral"
            >
              {t('text2364')}
            </Paragraph>
          )}
          {isLoading ? (
            [...Array(6)].map((_, index) => <BlogCardSkeleton key={index} />)
          ) : blogs.length > 0 ? (
            <>
              {blogs.map((blog, index) => (
                <BlogCard key={blog.id} blog={blog} headingTag="h2" imagePriority={index === 0} />
              ))}
            </>
          ) : (
            <Paragraph color="neutral" size="base" className="col-span-full text-center">
              {t('text2376')}
            </Paragraph>
          )}
        </div>
      </TabsContent>
      {blogsQuery.hasNextPage && (
        <div className="flex w-full items-center justify-center pr-4 md:pr-0">
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            isLoading={blogsQuery.isFetchingNextPage}
            variant={'white'}
            className="w-full sm:w-fit"
          >
            {t('text338')}
          </Button>
        </div>
      )}
    </Tabs>
  );
}
